export type NivelCatalogo =
  | "admin"
  | "editor"
  | "viewer"
  | "by_relation"
  | "no_access";

/** Un hit del índice main, tal como lo devuelve `GET /pim/products`. */
export interface CatalogHit {
  objectID: string;
  magentoProductId: number;
  sku: string;
  name: string;
  manufacturer: string | false;
  avgPrice: number;
  status: number | false;
  /**
   * El estado del producto (`vv_state`), que `status` no distingue: Pausado y Archivado son
   * el mismo `2`. Ausente en los records todavía sin reindexar (xcons-docs#380).
   */
  state?: "active" | "inactive" | "archived" | false;
  attributeSet: string | false;
  attributeSetId: number | false;
  /** Porcentaje de CON-P001 cubierto. */
  completeness: number;
  /** Las etiquetas de los campos que faltan: es lo que se puede facetear. */
  completenessMissing: string[];
  categoryNames?: string[];
  image?: string | false;
  /**
   * El nivel propio sobre el producto: `admin`, `editor` o `null` (sólo lectura), contando
   * el permiso global. Este campo y los dos de abajo los suma el listado para quien mira
   * (xcons-docs#430). Son opcionales: un API anterior a #430 no los manda, y la pantalla cae
   * a la regla general del nivel.
   */
  myLevel?: "admin" | "editor" | null;
  /** El dueño, o `null` = "Sin dueño", que es un estado válido. */
  owner?: ProductOwner | null;
  /** Las acciones en lote que puede ejecutar sobre esta fila. Vacío = sin checkbox. */
  bulkActions?: BulkAction[];
}

/**
 * Las acciones en lote del listado. **Es contrato con el API** (`bulk-actions.ts` de
 * xcon-api, xcons-docs#430): un conjunto cerrado, que se agrega en los dos lados.
 */
export type BulkAction =
  | "publish"
  | "pause"
  | "archive"
  | "approve"
  | "assignOwner"
  /** Borrador → En revisión, por `bulk/submit` (xcons-docs#453). */
  | "submit"
  /** Eliminar un borrador de alta, por `bulk/discard` (xcons-docs#473). */
  | "discard";

/** El dueño de un producto: la participación en `position=0`. */
export interface ProductOwner {
  contactId: number;
  userId: number | null;
  name: string;
}

export interface CatalogSearchResponse {
  /** El nivel del operador, que viaja con el listado. Ausente ⇒ tratar como viewer. */
  myCatalogLevel?: NivelCatalogo;
  hits: CatalogHit[];
  nbHits: number;
  page: number;
  nbPages: number;
  facets?: Record<string, Record<string, number>>;
}

/** Una fila de `GET /pim/drafts`. */
export interface DraftRow {
  /**
   * **Llega como string.** La PK de `product_pending_version` es `bigint`, y TypeORM
   * serializa los bigint como string para no perder precisión. Se coerciona al mandarlo
   * (el backend también lo acepta, pero el tipo tiene que decir la verdad).
   */
  id: number | string;
  sku: string;
  productId: number | null;
  state: "draft" | "review";
  name: string | null;
  manufacturer: string | null;
  attributeSetId: number | null;
  authorUserId: number;
  reviewReason: string | null;
  createdAt: string;
  updatedAt: string;
  /** Sale del propio `productId`: no hay campo extra. */
  neverPublished: boolean;
  /**
   * `approve` en las que están en revisión (xcons-docs#430) y `submit` en los borradores
   * (xcons-docs#453), cada una si quien mira puede.
   */
  bulkActions?: BulkAction[];
}

export interface CatalogAttribute {
  code: string;
  label: string | null;
  frontendInput: string | null;
  backendType: string | null;
  required: boolean;
  visible: boolean;
  options?: Array<{
    value: number;
    label: string;
    /**
     * La imagen del valor —el logo de una marca, el swatch de un color—, si tiene. La pide
     * xcons-docs#491: hasta que el API la mande, el recuadro de la ficha queda vacío.
     */
    image?: string | null;
    /**
     * Creado desde una ficha y **todavía sin revisar**: el producto que lo trajo no se aprobó
     * (xcons-docs#490). Lo manda el API (xcons-docs#491); la ficha lo marca "Valor nuevo".
     */
    nuevo?: boolean;
  }>;
}

export interface CatalogGroup {
  id: number;
  name: string;
  attributes: CatalogAttribute[];
}

/** `GET /pim/products/:sku`. */
export interface CatalogDetail {
  magentoProductId: number;
  sku: string;
  typeId: string;
  attributeSet: { id: number; name: string | null };
  createdAt: string;
  updatedAt: string;
  values: Record<string, unknown>;
  /** La etiqueta de las opciones elegidas: alcanza para el detalle read-only. */
  selectedLabels: Record<string, string | string[]>;
  /**
   * Los `option_id` elegidos que apuntan a una opción **borrada**, por `attribute_code`.
   * Hay decenas de miles (vv_model, vv_color…): sin esto se mostraba el número crudo.
   */
  orphanOptions?: Record<string, number[]>;
  groups: CatalogGroup[];
  /**
   * Lo que el producto tiene **fuera del EAV**, en la misma forma que consume el
   * guardado: el form lo lee y lo devuelve tal cual. Cualquier diferencia entre las dos
   * formas se convierte en un dato que se pierde al editar.
   */
  media?: Array<{
    file: string;
    /** `image` o `external-video`. El form lo devuelve: sin él, un video se guardaba como imagen. */
    mediaType?: string;
    /** Los datos del video; `null` para una imagen. El `file` de un video es su miniatura. */
    video?: ProductVideo | null;
    /** La URL pública que arma el API. Vacía si el ambiente no tiene la base de media. */
    url?: string;
    label?: string | null;
    position?: number;
    disabled?: boolean;
    roles?: string[];
  }>;
  categories?: Array<{
    categoryId: number;
    position?: number;
    /** Resuelto en la lectura: el tag lo necesita cuando la categoría es de otro árbol. */
    name?: string | null;
    /** El root del árbol donde cae. `null` para `Root Catalog`, que no es de ninguno. */
    rootCategoryId?: number | null;
  }>;
  /**
   * Los cuatro vínculos, siempre con las cuatro claves. Un API anterior a xcons-docs#476 no
   * trae `complementary`: quien lo lee cae a vacío.
   */
  links?: Record<LinkKind, ProductLink[]>;
  /** Las fichas técnicas de MageWorx asociadas. */
  attachments?: ProductAttachment[];
  /** Los árboles donde caen las categorías, con los stores que usa cada uno. */
  categoryRoots?: Array<{
    rootCategoryId: number;
    name: string | null;
    stores: string[];
  }>;
  stock?: { qty: number | null; isInStock: boolean } | null;
}

/**
 * Los cuatro vínculos entre productos que el PIM edita, con las claves del guardado. Los tres
 * primeros son alternativas; `complementary` es lo que se compra junto (xcons-docs#476).
 */
export type LinkKind = "related" | "crosssell" | "upsell" | "complementary";

/** Un producto vinculado. `sku` y `name` son para mostrarlo: el guardado usa `productId`. */
export interface ProductLink {
  productId: number;
  sku?: string;
  name?: string | null;
  position?: number;
}

export interface ProductVideo {
  url: string | null;
  title: string | null;
  provider: string | null;
  description: string | null;
}

/** Una ficha técnica de MageWorx. */
export interface ProductAttachment {
  attachmentId: number;
  name: string | null;
  type: string | null;
  size: number;
  isActive: boolean;
  /** El link externo, o el archivo en la media. Vacío si el ambiente no tiene la base. */
  url: string;
  /** Es un archivo subido: se abre por el API (`attachments/:id/file`), no por `url`. */
  hasFile?: boolean;
}

/** Las tres vistas del listado. Cada una sale de su propia fuente. */
/**
 * Las dos vistas del catálogo del admin, **por fuente y no por estado**: los publicados
 * salen del índice global y los no publicados de la base del admin, que es lo que hace
 * imposible paginarlos juntos.
 *
 * Borradores y pendientes **comparten fuente y tabla**, así que van en una sola lista con
 * el estado como columna filtrable: separarlos obligaba a saber de antemano en cuál de
 * las dos pestañas estaba lo que uno busca.
 */
/**
 * Una fila del listado: o un producto publicado —que sale del índice global— o una versión
 * que todavía no se publicó —que sale de la base del admin—. **Van en la misma lista**, con
 * el estado como columna filtrable.
 */
export type CatalogRow = CatalogHit | DraftRow;

/**
 * Distingue las dos fuentes por el campo que sólo una tiene. No se mira `objectID` ni
 * `magentoProductId`: una versión pendiente sobre un publicado **tiene** `productId`, así
 * que ese campo no separa nada. `state` sí: el índice no lo trae.
 */
export const esBorrador = (row: CatalogRow): row is DraftRow =>
  (row as DraftRow).state === "draft" || (row as DraftRow).state === "review";

/** Una fila de `GET /pim/products/:id/participants`. */
export interface ProductParticipant {
  /** El id de `permission_participation`: es lo que se borra. */
  id: number;
  contactId: number;
  userId: number | null;
  name: string;
  email: string | null;
  /** `editor` o `admin`: el producto tiene dos niveles, no cuatro. */
  value: string;
  position: number;
  /** `position === 0`. Lo resuelve el backend para que la regla viva en un lugar. */
  isOwner: boolean;
  /**
   * Sus stores y sellers, recortados a lo que ve quien mira (xcons-docs#460). Calculados: la
   * participación es global y no guarda por cuál se la sumó. Ausente en una API anterior.
   */
  permissions?: CargoDeOperador[];
}

export interface ProductParticipants {
  participants: ProductParticipant[];
  /** `null` en los heredados, y es un estado válido: nadie los creó desde el PIM. */
  owner: ProductParticipant | null;
  ownLevel: string | null;
  isCatalogAdmin: boolean;
  /**
   * El nivel del operador sobre el catálogo. **Ausente se trata como `viewer`**: con el
   * API viejo la pantalla queda en sólo lectura en vez de ofrecer botones que van a dar
   * 403. Falla cerrado.
   */
  catalogLevel?: "admin" | "editor" | "viewer" | "by_relation" | "no_access";
  canManage: boolean;
}

/** Un cargo de operador, con la forma de `GET /contacts/search` (xcons-docs#383). */
export type CargoDeOperador = {
  store?: string;
  seller?: string;
  /** El nombre del store o del seller. `null` sin cargar: el tag cae al código. */
  name?: string | null;
  /**
   * Sólo en un seller del panel de Permisos: los stores a los que está vinculado, dentro
   * del alcance de quien mira, para anidarlo debajo de cada uno (xcons-docs#402).
   */
  stores?: Array<{ store: string; name?: string | null }>;
};

export interface ProductParticipantCandidate {
  contactId: number;
  userId: number;
  name: string;
  email: string | null;
  /** Sus stores y sellers. Opcional: un API anterior a #383 no lo manda. */
  permissions?: CargoDeOperador[];
}

/** Un Admin de catálogo: el permiso global, sin niveles — se es o no se es. */
export interface CatalogAdmin {
  /** El nivel sobre el catálogo. Ausente con el API viejo: se lee como `admin`. */
  level?: "admin" | "editor" | "viewer" | "by_relation";
  userId: number;
  contactId: number | null;
  name: string;
  email: string | null;
  /** `false` ⇒ tiene el permiso global pero no entra a la sección: le falta `catalog`. */
  hasCatalogAccess: boolean;
  isSelf: boolean;
  /**
   * Sus stores y sellers, para agrupar el panel (xcons-docs#403). Opcional: un API anterior
   * a xcons-docs#402 no lo manda, y el panel cae a la lista sin grupos.
   */
  permissions?: CargoDeOperador[];
}

export interface CatalogAdmins {
  admins: CatalogAdmin[];
  /** Administrar el permiso global es tenerlo. Lo resuelve el backend. */
  canManage: boolean;
}

export interface CatalogAdminCandidate {
  userId: number;
  contactId: number;
  name: string;
  email: string | null;
  /**
   * Los stores y sellers de los que es operador, con la forma de `GET /contacts/search`
   * (xcons-docs#383). Opcional: un API anterior a #383 no lo manda.
   */
  permissions?: CargoDeOperador[];
}

export type ProductState =
  | "draft"
  | "review"
  | "active"
  | "inactive"
  | "archived";

/** Un hallazgo de CON-P001, tal como lo devuelve el validador. */
export interface ValidationFinding {
  field: string;
  severity: string;
  code: string;
  message: string;
  preexisting?: boolean;
}

/** Una acción de la barra de estado, con su gate ya resuelto por el backend. */
export interface TransitionOption {
  to: ProductState;
  gate: "editor" | "admin";
  /** Qué pasa, en una línea. Sale de la matriz del backend. */
  effect: string;
  allowed: boolean;
  blockedReason?: string;
  blockedCode?: "ADMIN_REQUIRED" | "NO_ACCESS" | "CON_P001_BLOCKING";
}

/** `GET /pim/products/:id/state`. */
export interface ProductStateView {
  magentoProductId: number;
  state: ProductState;
  /** Nace al publicar y no cambia; `null` = nunca se publicó. */
  urlKey: string | null;
  neverPublished: boolean;
  ownLevel: string | null;
  isCatalogAdmin: boolean;
  /**
   * El nivel del operador sobre el catálogo. **Ausente se trata como `viewer`**: con el
   * API viejo la pantalla queda en sólo lectura en vez de ofrecer botones que van a dar
   * 403. Falla cerrado.
   */
  catalogLevel?: "admin" | "editor" | "viewer" | "by_relation" | "no_access";
  transitions: TransitionOption[];
  validation: {
    blocking: ValidationFinding[];
    warnings: ValidationFinding[];
    /** Bloqueantes que ya valían sobre lo publicado: no frenan. */
    preexisting: ValidationFinding[];
  };
  pending: { id: number; state: "draft" | "review"; updatedAt: string } | null;
}

/** Las áreas del contrato que el diff sabe mostrar. */
export type DiffArea =
  | "identidad"
  | "valores"
  | "categorias"
  | "media"
  | "vinculos"
  | "adjuntos"
  | "stock";

/**
 * Una línea del diff. Sólo llegan las que cambian: el backend filtra lo igual, porque un
 * producto tiene ~90 atributos y listarlos todos convierte la revisión en un buscador.
 */
export interface DiffEntry {
  area: DiffArea;
  code: string;
  label: string;
  /** `null` en un alta: no hay nada publicado contra lo que comparar. */
  before: string | null;
  after: string | null;
  changed: boolean;
  /**
   * El valor que deja la versión es uno **creado desde la ficha y sin revisar** (xcons-docs#490):
   * aprobar el producto también lo aprueba. Lo manda el API (xcons-docs#491).
   */
  nuevoValor?: boolean;
    /** Lo publicado cambió DESPUÉS de armarse esta versión: aprobar lo pisaría. */
  conflict?: { base: string | null; live: string | null };
}

/** `GET /pim/drafts/:id` — la versión pendiente con su diff y qué puede hacer quien pregunta. */
export interface PendingDetail {
  id: number;
  sku: string;
  productId: number | null;
  neverPublished: boolean;
  state: "draft" | "review";
  name: string | null;
  manufacturer: string | null;
  attributeSetId: number | null;
  authorUserId: number;
  reviewReason: string | null;
  createdAt: string;
  updatedAt: string;
  /** El payload crudo: es la única fuente para rehidratar el form de un borrador. */
  payload?: {
    values?: Record<string, unknown>;
    categories?: Array<{ categoryId: number }>;
    media?: Array<{
      file: string;
      mediaType?: string;
      roles?: string[];
      position?: number;
    }>;
    links?: Partial<Record<LinkKind, Array<number | ProductLink>>>;
    attachmentIds?: number[];
    stock?: { qty?: number; isInStock?: boolean };
    attributeSetId?: number;
  };
  diff: {
    entries: DiffEntry[];
    counts: Record<DiffArea, number>;
    conflicts: Array<{ field: string; base: unknown; live: unknown }>;
  };
  /**
   * Resueltos por el backend, no deducidos acá: `canApprove` es la conjunción del estado y
   * del nivel, y repetir esa regla en el front es cómo se desincroniza de la que el service
   * aplica de verdad.
   */
  permissions: {
    /** Escribir un borrador pide nivel Editor: no hay participación que lo habilite. */
    canEdit: boolean;
    canApprove: boolean;
    canReject: boolean;
    canDiscard: boolean;
    canSubmit: boolean;
  };
}

// ── ABM de atributos y conjuntos (Fase 16) ───────────────────────────────────

/** Una fila de `GET /pim/attributes`. */
export interface AttributeRow {
  attributeId: number;
  code: string;
  label: string | null;
  frontendInput: string | null;
  backendType: string;
  isRequired: boolean;
  /** Los de sistema (`is_user_defined = 0`) no se editan desde el ABM. */
  isUserDefined: boolean;
  setCount: number;
  optionCount: number;
  /**
   * **Filas de valor, no productos distintos.** `visibility` tiene 236.265 filas para 50.327
   * productos, porque cada store view guarda su override. Es lo único que se puede contar
   * rápido sobre 2,3 M de filas (83 ms contra 2,9 s); el número exacto de productos lo da el
   * impacto, que es por atributo y va justo antes de decidir.
   */
  valueCount: number;
  catalog: Record<string, number>;
}

export interface AttributeDetailDto extends AttributeRow {
  options: Array<{
    optionId: number;
    label: string | null;
    sortOrder: number;
    /** El color (hex) o la imagen del valor, en los que la llevan (xcons-docs#491). */
    image?: string | null;
  }>;
  sets: Array<{
    attributeSetId: number;
    setName: string;
    attributeGroupId: number;
    groupName: string | null;
    sortOrder: number;
  }>;
}

/** `GET /pim/attributes/:code/impact` — el aviso que se muestra ANTES de guardar. */
export interface AttributeImpact {
  code: string;
  /** Productos DISTINTOS que lo usan. */
  products: number;
  /** Filas de valor, overrides por store incluidos: es lo que mira el bloqueo de tipo. */
  values: number;
  isStatic: boolean;
  feedsFacets: boolean;
  /** Valores distintos de la faceta que alimenta; `null` si no alimenta ninguna. */
  facetValues: number | null;
  usedInProductListing: boolean;
  frontendInputLocked: boolean;
  sets: Array<{ attributeSetId: number; setName: string; productCount: number }>;
}

export interface AttributeSetRow {
  attributeSetId: number;
  name: string;
  sortOrder: number;
  productCount: number;
  /** Asignaciones en `eav_entity_attribute`, huérfanas incluidas: el editor muestra menos. */
  attributeCount: number;
  groupCount: number;
}

export interface AttributeSetGroupDto {
  groupId: number;
  groupName: string;
  sortOrder: number;
  attributes: Array<{
    attributeId: number;
    code: string;
    label: string | null;
    frontendInput: string | null;
    isRequired: boolean;
    sortOrder: number;
  }>;
}

/** `GET /pim/attribute-sets/:id/groups` — la vista del editor. */
export interface AttributeSetDetailDto {
  attributeSetId: number;
  name: string;
  sortOrder: number;
  productCount: number;
  groups: AttributeSetGroupDto[];
}

// ── Catálogo por Store (Fase 18) ─────────────────────────────────────────────

export interface CategoryNode {
  categoryId: number;
  parentId: number;
  name: string;
  urlKey: string | null;
  isActive: boolean;
  includeInMenu: boolean;
  position: number;
  level: number;
  /** Lo que dice la columna de Magento: TODA la descendencia, no las hijas directas. */
  childrenCount: number;
  productCount: number;
  children: CategoryNode[];
}

/** `GET /pim/stores/:storeCode/catalog/categories`. */
export interface StoreCatalogTree {
  rootCategoryId: number;
  catalogMode: "manual_categories" | "auto_from_sellers";
  /** Su root es la de XCONS: comparte el árbol del catálogo unificado. */
  usesDefaultTree: boolean;
  nodes: CategoryNode[];
}

// ── Masivas e importador (Fase 21) ───────────────────────────────────────────

export interface BulkItemResult {
  id: number;
  ok: boolean;
  /** El código del error: es lo que permite AGRUPAR ("12 no cumplen CON-P001"). */
  code?: string;
  message?: string;
  /** Qué le falta, cuando no cumple CON-P001 (xcons-docs#453). */
  findings?: string[];
}

export interface BulkResult {
  total: number;
  ok: number;
  failed: number;
  items: BulkItemResult[];
  indexed?: number;
  /** Sólo en la masiva de categorías: cuántas quedaron esperando aprobación. */
  pendientes?: number;
  directos?: number;
  /**
   * De los `ok`, cuántos cambiaron y cuántos ya estaban así: asignar a quien ya es dueño,
   * publicar lo que ya está activo, mandar a revisión lo que ya está (xcons-docs#453).
   */
  changed?: number;
  unchanged?: number;
}

export interface ImportRun {
  id: number;
  filename: string | null;
  status: "running" | "done" | "failed";
  total: number;
  drafts: number;
  pending: number;
  direct: number;
  failed: number;
  /** Filas de existentes que no cambiaban nada (xcons-docs#468). Ausente en un API anterior. */
  unchanged?: number;
  createdAt: string;
  finishedAt: string | null;
}

export interface ImportRowResult {
  id: number;
  lineNumber: number;
  sku: string | null;
  status: "draft" | "pending" | "direct" | "failed" | "unchanged";
  magentoProductId: number | null;
  pendingId: number | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface ImportReport {
  run: ImportRun | null;
  rows: ImportRowResult[];
}

/**
 * Una fila de la tabla de Stores de la ficha: dónde se ofrece el producto y quién lo
 * ofrece ahí. `sellers` ya viene recortado por lo que administra quien pregunta.
 */
/** Con qué alcance armó el backend la respuesta, para poder decirlo en pantalla. */
export interface OfferScope {
  level: "catalog_admin" | "store" | "seller" | "none";
  storeCodes: string[] | null;
  sellerCodes: string[] | null;
}

export interface StoreOfferRow {
  storeCode: string;
  catalogMode: string;
  rootCategoryId: number;
  /** Si lo ofrece alguno de mis sellers, no si lo ofrece la tienda. */
  offered: boolean;
  sellers: Array<{ code: string; name: string | null }>;
}

/** La visibilidad en una tienda donde quien mira tiene permiso de store. */
export interface StoreVisibilityRow {
  storeCode: string;
  visible: boolean;
  reason?: string;
}

export interface ProductStoresResponse {
  scope: OfferScope;
  rows: StoreOfferRow[];
  /** Sólo las tiendas con permiso de store: la de otras es información de la competencia. */
  visibility?: StoreVisibilityRow[];
}
